import { asNullableNumber as financeNumber } from './common';
import {
  asArray,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import type { OrderSettlementEntry, OrderSettlementStatus } from '../modules/orders/types';

/** Invoice-ledger view of one order (GET /orders/:id/settlement). */
export interface OrderSettlementDto {
  costStatus?: 'KNOWN' | 'PENDING';
  planned: {
    revenue?: number;
    totalCost?: number;
    margin?: number;
    marginPercent?: number;
  };
  actual: {
    revenue: number;
    totalCost: number | null;
    margin: number | null;
    marginPercent: number | null;
  };
  settlementStatus: OrderSettlementStatus;
  settled: {
    revenue: number;
    totalCost: number | null;
    margin: number | null;
    marginPercent: number | null;
  } | null;
  entries: OrderSettlementEntry[];
  /** WP2: order lines added after the sales invoice, not settled until a correction includes them. */
  unsettledLines: Array<{ id: string; name: string }>;
  /** WP2: stock issued beyond the invoiced quantity (cost outside the settlement). */
  issuedOverInvoice: Array<{ orderProductId: string; name: string; unit: string; quantity: number }>;
}

export function normalizeOrderSettlement(raw: unknown, entity = 'orders.settlement'): OrderSettlementDto {
  const record = asRecord(raw);
  const planned = asRecord(record?.planned);
  const actual = asRecord(record?.actual);
  const settled = asRecord(record?.settled);
  const settlementStatus = record?.settlementStatus === 'PARTIALLY_SETTLED' ? 'PARTIALLY_SETTLED' : 'OPEN';

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    costStatus: record?.costStatus === 'PENDING' ? 'PENDING' : 'KNOWN',
    planned: {
      revenue: planned?.revenue == null ? undefined : asNumber(planned.revenue),
      totalCost: planned?.totalCost == null ? undefined : asNumber(planned.totalCost),
      margin: planned?.margin == null ? undefined : asNumber(planned.margin),
      marginPercent: planned?.marginPercent == null ? undefined : asNumber(planned.marginPercent),
    },
    actual: {
      revenue: asNumber(actual?.revenue),
      totalCost: financeNumber(actual?.totalCost),
      margin: financeNumber(actual?.margin),
      marginPercent: financeNumber(actual?.marginPercent),
    },
    settlementStatus,
    settled: settled ? {
      revenue: asNumber(settled.revenue),
      totalCost: financeNumber(settled.totalCost),
      margin: financeNumber(settled.margin),
      marginPercent: financeNumber(settled.marginPercent),
    } : null,
    entries: asArray(record?.entries, (item, index) => normalizeOrderSettlementEntry(item, `${entity}.entries[${index}]`)),
    unsettledLines: asArray(record?.unsettledLines, (item) => {
      const line = asRecord(item);
      return { id: asString(line?.id), name: asString(line?.name) };
    }),
    issuedOverInvoice: asArray(record?.issuedOverInvoice, (item) => {
      const line = asRecord(item);
      return { orderProductId: asString(line?.orderProductId), name: asString(line?.name), unit: asString(line?.unit), quantity: asNumber(line?.quantity) };
    }),
  };
}

function normalizeOrderSettlementEntry(raw: unknown, entity: string): OrderSettlementEntry {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const kind = record?.kind === 'CORRECTION' ? 'CORRECTION' : 'PARTIAL';

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    kind,
    recognizedAt: asString(record?.recognizedAt),
    revenue: asNumber(record?.revenue),
    totalCost: financeNumber(record?.totalCost),
    margin: financeNumber(record?.margin),
    marginPercent: financeNumber(record?.marginPercent),
    note: asOptionalString(record?.note) ?? null,
    createdById: asOptionalString(record?.createdById) ?? null,
    createdByName: asOptionalString(record?.createdByName) ?? null,
    createdByEmail: asOptionalString(record?.createdByEmail) ?? null,
    createdAt: asOptionalString(record?.createdAt),
    correctionOfId: asOptionalString(record?.correctionOfId) ?? null,
  };
}
